import { useState, useMemo, useEffect, type CSSProperties } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  listFestivals,
  createFestival,
  createStage,
  deleteFestival,
  updateFestival,
} from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { useActiveOrgId } from "@/hooks/useActiveOrg";
import { ONBOARDING_PROGRESS_QUERY_KEY } from "@/hooks/useOnboardingProgress";
import { Skeleton } from "@/components/ui/skeleton";
import { Stagger } from "@/components/Animate";
import { OrgPageTitle } from "@/components/OrgPageTitle";
import { Button, Segmented, EmptyState, Input } from "@/components/ds";
import {
  Calendar,
  MapPin,
  Plus,
  Trash2,
  Layers,
  Search,
  X,
  Repeat,
  ArrowRight,
  CalendarPlus,
} from "lucide-react";

const FESTIVAL_COLORS = [
  { name: "rose", hex: "#f43f5e" },
  { name: "orange", hex: "#f97316" },
  { name: "amber", hex: "#f59e0b" },
  { name: "emerald", hex: "#10b981" },
  { name: "teal", hex: "#14b8a6" },
  { name: "cyan", hex: "#06b6d4" },
  { name: "blue", hex: "#3b82f6" },
  { name: "indigo", hex: "#6366f1" },
  { name: "violet", hex: "#8b5cf6" },
  { name: "fuchsia", hex: "#d946ef" },
  { name: "pink", hex: "#ec4899" },
];

function getColorHex(color: string | null): string {
  if (!color) return "var(--tr-stage-main)";
  return FESTIVAL_COLORS.find((c) => c.name === color)?.hex ?? "#71717a";
}

type FestStatus = "live" | "upcoming" | "past";

function festivalStatus(startStr: string, endStr: string): FestStatus {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");
  end.setHours(23, 59, 59, 999);
  if (today < start) return "upcoming";
  if (today > end) return "past";
  return "live";
}

const STATUS_META: Record<FestStatus, { label: string; color: string }> = {
  live: { label: "Live", color: "var(--tr-ok)" },
  upcoming: { label: "Upcoming", color: "var(--tr-accent)" },
  past: { label: "Wrapped", color: "var(--tr-text-3)" },
};

function StatusPill({ status }: { status: FestStatus }) {
  const m = STATUS_META[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-pill border-2 px-3 py-0.5 text-xs font-semibold"
      style={
        {
          color: m.color,
          borderColor: `color-mix(in oklab, ${m.color} 35%, transparent)`,
          background: `color-mix(in oklab, ${m.color} 12%, transparent)`,
        } as CSSProperties
      }
    >
      <i className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(dateStr + "T00:00:00");
  return Math.round((start.getTime() - today.getTime()) / 86400000);
}

export function FestivalListPage() {
  const queryClient = useQueryClient();
  const activeOrgId = useActiveOrgId();
  const [showCreate, setShowCreate] = useState(false);
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    name: "",
    start_date: "",
    end_date: "",
    location: "",
    color: "blue",
    recurring: false,
    day_of_week: 6,
  });
  const [wizardStages, setWizardStages] = useState<{ name: string; fomet_count: number }[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "upcoming" | "past">("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: festivals, isLoading } = useQuery({
    queryKey: ["festivals", activeOrgId],
    queryFn: () => listFestivals(activeOrgId),
  });

  const [isCreating, setIsCreating] = useState(false);

  const handleWizardSubmit = async () => {
    setIsCreating(true);
    try {
      const orgId = useAuthStore.getState().activeOrganizationId;
      const festival = await createFestival({
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        location: form.location || undefined,
        color: form.color || undefined,
        recurrence: form.recurring
          ? { type: "weekly", day_of_week: form.day_of_week }
          : undefined,
        organization_id: orgId || undefined,
      });
      const validStages = wizardStages.filter((s) => s.name.trim());
      if (validStages.length > 0) {
        await Promise.all(
          validStages.map((s) =>
            createStage(festival.id, { name: s.name.trim(), fomet_count: s.fomet_count }),
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["festivals"] });
      queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_QUERY_KEY });
      setShowCreate(false);
      setWizardStep(1);
      setForm({
        name: "",
        start_date: "",
        end_date: "",
        location: "",
        color: "blue",
        recurring: false,
        day_of_week: 6,
      });
      setWizardStages([]);
      toast.success(
        validStages.length > 0
          ? `Festival created with ${validStages.length} stage${validStages.length !== 1 ? "s" : ""}`
          : "Festival created",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create festival");
    } finally {
      setIsCreating(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: deleteFestival,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["festivals"] });
      toast.success("Festival deleted");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to delete festival");
    },
  });

  const enriched = useMemo(() => {
    if (!festivals) return [];
    return festivals.map((f) => ({ ...f, status: festivalStatus(f.start_date, f.end_date) }));
  }, [festivals]);

  // Unique locations for the location filter (parity with the pre-redesign list).
  const locations = useMemo(() => {
    const set = new Set<string>();
    for (const f of enriched) if (f.location) set.add(f.location);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [enriched]);

  const filtered = useMemo(() => {
    return enriched.filter((f) => {
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filter === "upcoming" && f.status === "past") return false;
      if (filter === "past" && f.status !== "past") return false;
      if (locationFilter !== "all" && f.location !== locationFilter) return false;
      return true;
    });
  }, [enriched, search, filter, locationFilter]);

  // Featured = the next upcoming (or live) festival, hidden on Past filter.
  const featured = useMemo(() => {
    if (filter === "past") return null;
    const candidates = filtered
      .filter((f) => f.status !== "past")
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return candidates.find((f) => f.status === "live") ?? candidates[0] ?? null;
  }, [filtered, filter]);

  const gridFestivals = useMemo(
    () => filtered.filter((f) => f.id !== featured?.id),
    [filtered, featured],
  );

  const counts = useMemo(
    () => ({
      all: enriched.length,
      upcoming: enriched.filter((f) => f.status !== "past").length,
      past: enriched.filter((f) => f.status === "past").length,
    }),
    [enriched],
  );

  const handleStepOneNext = (e: React.FormEvent) => {
    e.preventDefault();
    setWizardStep(2);
    if (wizardStages.length === 0) setWizardStages([{ name: "", fomet_count: 1 }]);
  };

  const handleDelete = (e: React.MouseEvent, id: string, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm(`Delete "${name}"? This will also delete all stages and slots.`)) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <OrgPageTitle
          subtitle={
            festivals
              ? `${festivals.length} festival${festivals.length !== 1 ? "s" : ""}`
              : "Manage your festival events"
          }
        >
          Festivals
        </OrgPageTitle>
        <Button variant="accent" icon={Plus} onClick={() => setShowCreate(true)}>
          <span className="hidden sm:inline">Create festival</span>
          <span className="sm:hidden">New</span>
        </Button>
      </div>

      {/* Filter + search */}
      {festivals && festivals.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            value={filter}
            onChange={setFilter}
            ariaLabel="Filter festivals"
            options={[
              { value: "all", label: "All", count: counts.all },
              { value: "upcoming", label: "Upcoming", count: counts.upcoming },
              { value: "past", label: "Past", count: counts.past },
            ]}
          />
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search festivals…"
              className="pl-9"
            />
          </div>
          {locations.length > 0 && (
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="input w-auto pl-9 pr-3"
                aria-label="Filter by location"
              >
                <option value="all">All locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Featured */}
      {!isLoading && featured && <FeaturedCard festival={featured} formatDate={formatDate} />}

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card p-5">
              <div className="flex items-start gap-3.5">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3.5 w-40" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-5 w-16 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      ) : gridFestivals.length > 0 ? (
        <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={60} delay={80}>
          {gridFestivals.map((f) => (
            <FestivalCard key={f.id} festival={f} formatDate={formatDate} onDelete={handleDelete} />
          ))}
        </Stagger>
      ) : featured ? null : festivals && festivals.length > 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Search}
            title="No festivals match your filters"
            subtitle="Try a different filter or search term."
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setFilter("all");
                  setLocationFilter("all");
                }}
              >
                Clear filters
              </Button>
            }
          />
        </div>
      ) : (
        <div className="card p-8">
          <EmptyState
            icon={CalendarPlus}
            title="No festivals yet"
            subtitle="Create your first festival to start building the lineup."
            action={
              <Button variant="accent" size="sm" icon={Plus} onClick={() => setShowCreate(true)}>
                Create festival
              </Button>
            }
          />
        </div>
      )}

      {showCreate && (
        <CreateWizard
          form={form}
          setForm={setForm}
          wizardStep={wizardStep}
          setWizardStep={setWizardStep}
          wizardStages={wizardStages}
          setWizardStages={setWizardStages}
          isCreating={isCreating}
          onClose={() => {
            setShowCreate(false);
            setWizardStep(1);
          }}
          onStepOne={handleStepOneNext}
          onSubmit={handleWizardSubmit}
        />
      )}
    </div>
  );
}

interface FestivalListItem {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  location: string | null;
  stage_count: number;
  color: string | null;
  recurrence: { type: string; day_of_week: number } | null;
  status: FestStatus;
}

function FeaturedCard({
  festival: f,
  formatDate,
}: {
  festival: FestivalListItem;
  formatDate: (d: string) => string;
}) {
  const hex = getColorHex(f.color);
  const days = daysUntil(f.start_date);
  return (
    <Link
      to={`/festivals/${f.id}`}
      data-ds-pane
      className="card relative block overflow-hidden p-6 transition-colors hover:border-line-3"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 90% 120% at 90% 10%, color-mix(in oklab, ${hex} 22%, transparent) 0%, transparent 60%)`,
        }}
      />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2">
            <StatusPill status={f.status} />
            {f.status === "upcoming" && days >= 0 && (
              <span className="font-mono text-xs text-ink-3">
                in {days} day{days !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <h2 className="text-[26px] font-bold tracking-[-0.025em]" data-ds-display>
            {f.name}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[12px] text-ink-3">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.6} />
              {formatDate(f.start_date)} — {formatDate(f.end_date)}
            </span>
            {f.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" strokeWidth={1.6} />
                {f.location}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" strokeWidth={1.6} />
              {f.stage_count} stage{f.stage_count !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <span className="btn btn--accent shrink-0">
          Open festival
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
        </span>
      </div>
    </Link>
  );
}

function FestivalCard({
  festival: f,
  formatDate,
  onDelete,
}: {
  festival: FestivalListItem;
  formatDate: (d: string) => string;
  onDelete: (e: React.MouseEvent, id: string, name: string) => void;
}) {
  const hex = getColorHex(f.color);
  return (
    <div className="group relative h-full">
      <Link
        to={`/festivals/${f.id}`}
        className="card flex h-full flex-col p-5 transition-colors hover:border-line-3"
      >
        <div className="flex items-start gap-3.5">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-sm text-sm font-bold text-white/90"
            style={{ backgroundColor: hex, boxShadow: `0 4px 14px -3px ${hex}50` }}
          >
            {f.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-[15px] font-bold tracking-tight text-ink">{f.name}</h3>
              {/* Same slot swaps between status pill (rest) and the hover
                  actions (colour + delete) so nothing ever overlaps. */}
              <div className="relative shrink-0">
                <span className="block transition-opacity duration-200 group-hover:opacity-0 group-focus-within:opacity-0">
                  <StatusPill status={f.status} />
                </span>
                <div className="absolute inset-y-0 right-0 flex items-center justify-end gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
                  <ColorPicker festivalId={f.id} current={f.color} />
                  <button
                    type="button"
                    onClick={(e) => onDelete(e, f.id, f.name)}
                    className="rounded-md p-1.5 text-ink-3 transition-colors hover:bg-panel-2 hover:text-bad"
                    title="Delete festival"
                    aria-label={`Delete ${f.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5 font-mono text-[11px] tabular-nums text-ink-3">
              <Calendar className="h-3 w-3 shrink-0" strokeWidth={1.6} />
              {formatDate(f.start_date)} — {formatDate(f.end_date)}
            </div>
            {f.recurrence?.type === "weekly" && (
              <div className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-accent">
                <Repeat className="h-2.5 w-2.5" strokeWidth={1.6} />
                Every{" "}
                {
                  ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][
                    f.recurrence.day_of_week
                  ]
                }
              </div>
            )}
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between pt-4">
          {f.location ? (
            <span className="flex items-center gap-1.5 text-[11px] text-ink-3">
              <MapPin className="h-3 w-3 shrink-0" strokeWidth={1.6} />
              {f.location}
            </span>
          ) : (
            <span />
          )}
          <span
            className="flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-medium tabular-nums"
            style={{ color: `${hex}cc`, backgroundColor: `${hex}18` }}
          >
            <Layers className="h-2.5 w-2.5" strokeWidth={1.6} />
            {f.stage_count} stage{f.stage_count !== 1 ? "s" : ""}
          </span>
        </div>
      </Link>
    </div>
  );
}

function ColorPicker({ festivalId, current }: { festivalId: string; current: string | null }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (color: string) => updateFestival(festivalId, { color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["festivals"] });
      setOpen(false);
    },
  });
  const hex = getColorHex(current);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className="rounded-md p-1 transition-colors hover:bg-panel-2"
        title="Change color"
        aria-label="Change festival color"
      >
        <div className="h-4 w-4 rounded-full border border-white/20" style={{ backgroundColor: hex }} />
      </button>
      {open && (
        <div
          className="card absolute right-0 top-8 z-50 p-3 shadow-xl"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="flex flex-wrap gap-2.5" style={{ width: "160px" }}>
            {FESTIVAL_COLORS.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  mutation.mutate(c.name);
                }}
                className={`h-5 w-5 rounded-full transition-all hover:scale-125 ${
                  current === c.name ? "ring-2 ring-accent ring-offset-1 ring-offset-panel" : ""
                }`}
                style={{ backgroundColor: c.hex }}
                aria-label={c.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type WizardForm = {
  name: string;
  start_date: string;
  end_date: string;
  location: string;
  color: string;
  recurring: boolean;
  day_of_week: number;
};

function CreateWizard({
  form,
  setForm,
  wizardStep,
  setWizardStep,
  wizardStages,
  setWizardStages,
  isCreating,
  onClose,
  onStepOne,
  onSubmit,
}: {
  form: WizardForm;
  setForm: (f: WizardForm) => void;
  wizardStep: 1 | 2;
  setWizardStep: (s: 1 | 2) => void;
  wizardStages: { name: string; fomet_count: number }[];
  setWizardStages: (s: { name: string; fomet_count: number }[]) => void;
  isCreating: boolean;
  onClose: () => void;
  onStepOne: (e: React.FormEvent) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="card w-full max-w-md p-6">
        <div className="mb-4 flex items-center gap-3">
          <div
            className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
              wizardStep === 1 ? "bg-accent text-[#042027]" : "bg-panel-3 text-ink-3"
            }`}
          >
            1
          </div>
          <div className="h-px flex-1 bg-line-2" />
          <div
            className={`grid h-6 w-6 place-items-center rounded-full text-xs font-semibold ${
              wizardStep === 2 ? "bg-accent text-[#042027]" : "bg-panel-3 text-ink-4"
            }`}
          >
            2
          </div>
        </div>

        {wizardStep === 1 ? (
          <form onSubmit={onStepOne} className="space-y-4">
            <h3 className="text-lg font-semibold">Festival details</h3>
            <div className="space-y-1.5">
              <label className="text-sm text-ink-2">Name</label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm text-ink-2">Start date</label>
                <Input
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-ink-2">End date</label>
                <Input
                  type="date"
                  required
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-ink-2">Location</label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-ink-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {FESTIVAL_COLORS.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.name })}
                    className={`h-7 w-7 rounded-full transition-all ${
                      form.color === c.name
                        ? "scale-110 ring-2 ring-accent ring-offset-2 ring-offset-panel"
                        : "hover:scale-110"
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.name}
                    aria-label={c.name}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="checkbox"
                  checked={form.recurring}
                  onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                  className="rounded border-line-3 bg-panel"
                />
                <Repeat className="h-3.5 w-3.5" strokeWidth={1.6} />
                Recurring event
              </label>
              {form.recurring && (
                <div className="space-y-2 rounded-ds-sm border border-line-2 bg-panel-2 p-3">
                  <p className="text-xs text-ink-3">Select the day of the week</p>
                  <div className="flex gap-1.5">
                    {(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const).map((label, i) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setForm({ ...form, day_of_week: i })}
                        className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-colors ${
                          form.day_of_week === i
                            ? "bg-accent text-[#042027]"
                            : "bg-panel-3 text-ink-3 hover:text-ink"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="accent" size="sm" type="submit">
                Next
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Add stages</h3>
            <p className="text-xs text-ink-3">
              Add the stages for your festival. You can also do this later.
            </p>
            <div className="space-y-2">
              {wizardStages.map((stage, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={stage.name}
                    onChange={(e) =>
                      setWizardStages(
                        wizardStages.map((s, j) => (j === i ? { ...s, name: e.target.value } : s)),
                      )
                    }
                    placeholder={`Stage ${i + 1} name`}
                  />
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-ink-3">Fomet</label>
                    <Input
                      type="number"
                      min={1}
                      max={4}
                      value={stage.fomet_count}
                      onChange={(e) =>
                        setWizardStages(
                          wizardStages.map((s, j) =>
                            j === i
                              ? { ...s, fomet_count: Math.max(1, parseInt(e.target.value) || 1) }
                              : s,
                          ),
                        )
                      }
                      className="w-14"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setWizardStages(wizardStages.filter((_, j) => j !== i))}
                    className="rounded p-1 text-ink-3 hover:text-bad"
                    aria-label="Remove stage"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setWizardStages([...wizardStages, { name: "", fomet_count: 1 }])}
              className="flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.6} />
              Add stage
            </button>
            <div className="flex justify-end gap-2 border-t border-line-2 pt-4">
              <Button variant="ghost" size="sm" onClick={() => setWizardStep(1)}>
                Back
              </Button>
              <Button variant="accent" size="sm" onClick={onSubmit} disabled={isCreating}>
                {isCreating
                  ? "Creating…"
                  : wizardStages.some((s) => s.name.trim())
                    ? "Create with stages"
                    : "Skip & create"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
